/**
 * Zoom con los dedos en el lector.
 *
 * Son dos mecanismos distintos a proposito, uno por modo, porque el problema
 * no es el mismo:
 *
 * - **Paginado** (`useZoomPagina`): la pagina es una sola imagen que cabe
 *   entera en pantalla y ahi no hay scroll ninguno, asi que ampliar es un
 *   `transform` sobre ella y moverla es arrastrar con un dedo. No toca nada
 *   mas del lector.
 * - **Tira vertical** (`useZoomTira`): aqui SI hay scroll, y de ese scroll
 *   dependen el guardado de la posicion, la restauracion al reabrir y el
 *   observador que dice por que pagina vas. Un `transform` no cambia la
 *   maquetacion: el scroller seguiria midiendo lo de antes y las tres cosas
 *   se romperian. Por eso la tira se amplia cambiandole el **ancho** al
 *   contenedor —las imagenes van al 100% de el y crecen de verdad—, y asi el
 *   desplazamiento vertical sigue siendo el nativo del navegador y el
 *   horizontal aparece solo.
 *
 * Safari no da un evento de pellizco (`user-scalable=no` en el viewport apaga
 * el zoom de la pagina, que es lo que se quiere: ampliar la imagen y no la
 * interfaz), asi que se calcula a mano con los dos toques.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

/** Mas alla de esto no se lee mejor: solo se ven los pixeles mas gordos. */
const MAXIMO = 4
/** Por debajo se entiende que se queria quitar el zoom, y se quita del todo. */
const MINIMO_UTIL = 1.06
/** Movimiento a partir del cual el dedo estaba arrastrando y no tocando. */
const ARRASTRE = 10

interface Punto {
  x: number
  y: number
}

interface Vista {
  escala: number
  x: number
  y: number
}

/** Caja de la imagen sin ampliar, en coordenadas del lienzo. */
interface Medidas {
  izq: number
  arr: number
  ancho: number
  alto: number
  anchoLienzo: number
  altoLienzo: number
}

export interface Zoom {
  /** Ampliacion actual. 1 es "sin zoom". */
  escala: number
  /**
   * Dice si el ultimo toque fue un gesto (pellizco o arrastre) y lo da por
   * consumido. Ampliar acaba soltando un `click`, y sin esto pasaba de pagina.
   */
  consumirGesto: () => boolean
  /** Vuelve al tamano normal. */
  quitar: () => void
  /**
   * Pega el contenido a un lado del lienzo cuando no cabe a lo ancho. Se usa
   * al abrir una pagina doble, para empezar por donde empieza la lectura.
   */
  colocar: (lado: 'izq' | 'der') => void
}

function separacion(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function centro(a: Touch, b: Touch): Punto {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 }
}

function entre(valor: number, minimo: number, maximo: number): number {
  return Math.min(maximo, Math.max(minimo, valor))
}

/** Redondea al 1 exacto lo que se le acerque, para no dejar zooms de 1.02. */
function limpia(escala: number): number {
  return escala < MINIMO_UTIL ? 1 : entre(escala, 1, MAXIMO)
}

/*
 * En los dos casos la ampliacion se escribe **directamente en el DOM** durante
 * el gesto y solo se lleva a React al soltar. Un `setState` por cada
 * `touchmove` volveria a pintar las hasta cuarenta paginas de la tira sesenta
 * veces por segundo, y el pellizco se arrastraria.
 */

/**
 * Pellizco en modo paginado: `transform` sobre el envoltorio de la pagina.
 *
 * @param caja         el lienzo, que es lo que recibe los toques
 * @param contenidoRef el envoltorio que se amplia
 */
export function useZoomPagina(
  caja: HTMLElement | null,
  contenidoRef: RefObject<HTMLElement | null>,
  activo: boolean,
): Zoom {
  const vista = useRef<Vista>({ escala: 1, x: 0, y: 0 })
  const [escala, setEscala] = useState(1)
  const hubogesto = useRef(false)

  const pintar = useCallback(() => {
    const el = contenidoRef.current
    if (!el) return
    const { escala: s, x, y } = vista.current
    /*
     * Quieta y sin ampliar se deja sin transform: asi no crea bloque
     * contenedor para el indicador de carga, que va posicionado sobre el
     * lector entero.
     *
     * Lo que **no** vale es mirar solo la escala. Una pagina doble se mueve
     * sin ampliar nada —es mas ancha que la pantalla de por si— y con esa
     * condicion el desplazamiento se pintaba como nada: el banco decia
     * "arrastre de 100 px mueve: 0" con el gesto ya reconocido.
     */
    const quieta = s === 1 && x === 0 && y === 0
    el.style.transform = quieta ? '' : `translate3d(${x}px, ${y}px, 0) scale(${s})`
  }, [contenidoRef])

  const quitar = useCallback(() => {
    if (vista.current.escala === 1 && vista.current.x === 0 && vista.current.y === 0) return
    vista.current = { escala: 1, x: 0, y: 0 }
    pintar()
    setEscala(1)
  }, [pintar])

  /**
   * Pega la pagina a un lado sin tocar la ampliacion.
   *
   * Es para abrir una doble por donde se empieza a leer: a alto completo no
   * cabe a lo ancho, y centrada deja fuera de la pantalla la primera vineta.
   * Los limites son los mismos que usa el arrastre, asi que lo que deja es
   * una posicion valida y no hay que corregirla luego.
   */
  const colocar = useCallback(
    (lado: 'izq' | 'der') => {
      const contenido = contenidoRef.current
      if (!caja || !contenido) return
      const pagina = contenido.querySelector('.pagina-doble, img')
      if (!pagina) return
      const lienzo = caja.getBoundingClientRect()
      const suya = pagina.getBoundingClientRect()
      const { escala: s, x } = vista.current
      // Medidas sin ampliar, deshaciendo la vista de ahora
      const ancho = suya.width / s
      const izq = (suya.left - lienzo.left - x) / s
      // Si cabe entera no hay nada que colocar: ya esta centrada
      if (ancho * s <= lienzo.width) return
      vista.current = {
        ...vista.current,
        x: lado === 'izq' ? -izq * s : lienzo.width - ancho * s - izq * s,
      }
      pintar()
    },
    [caja, contenidoRef, pintar],
  )

  useEffect(() => {
    if (!caja || !activo) return

    type Gesto =
      | { tipo: 'pellizco'; medidas: Medidas; separacion0: number; centro0: Punto; vista0: Vista }
      | { tipo: 'arrastre'; medidas: Medidas; punto0: Punto; vista0: Vista }
    let gesto: Gesto | null = null

    /**
     * La caja de la imagen **sin ampliar**, deshaciendo la vista actual. Se
     * mide con `getBoundingClientRect` y no con `offsetLeft` porque este
     * ultimo va contra el ancestro posicionado, que aqui es el lector entero.
     */
    function medir(): Medidas {
      const lienzo = caja!.getBoundingClientRect()
      /*
       * Con dos mitades unidas lo que se mide es la doble entera y no su
       * primera mitad: `querySelector` devuelve el primero que aparece en el
       * documento, y el envoltorio va por delante de sus imagenes. Midiendo
       * solo la primera, los limites dejaban la mitad derecha fuera de
       * alcance y no habia forma de llegar a ella arrastrando.
       */
      const img = contenidoRef.current?.querySelector('.pagina-doble, img')
      const { escala: s, x, y } = vista.current
      if (!img) {
        return {
          izq: 0,
          arr: 0,
          ancho: lienzo.width,
          alto: lienzo.height,
          anchoLienzo: lienzo.width,
          altoLienzo: lienzo.height,
        }
      }
      const suya = img.getBoundingClientRect()
      return {
        izq: (suya.left - lienzo.left - x) / s,
        arr: (suya.top - lienzo.top - y) / s,
        ancho: suya.width / s,
        alto: suya.height / s,
        anchoLienzo: lienzo.width,
        altoLienzo: lienzo.height,
      }
    }

    /**
     * No deja que la imagen se despegue de la pantalla: mientras sobresalga,
     * sus bordes no pueden entrar; cuando ya cabe entera, se centra sola.
     */
    function ajustar(m: Medidas, s: number, x: number, y: number): Vista {
      const ancho = m.ancho * s
      const alto = m.alto * s
      return {
        escala: s,
        x:
          ancho <= m.anchoLienzo
            ? (m.anchoLienzo - ancho) / 2 - m.izq * s
            : entre(x, m.anchoLienzo - ancho - m.izq * s, -m.izq * s),
        y:
          alto <= m.altoLienzo
            ? (m.altoLienzo - alto) / 2 - m.arr * s
            : entre(y, m.altoLienzo - alto - m.arr * s, -m.arr * s),
      }
    }

    function alEmpezar(e: TouchEvent) {
      hubogesto.current = false
      const uno = e.touches[0]
      const dos = e.touches[1]
      if (uno && dos) {
        gesto = {
          tipo: 'pellizco',
          medidas: medir(),
          separacion0: separacion(uno, dos),
          centro0: centro(uno, dos),
          vista0: { ...vista.current },
        }
      } else if (uno) {
        /*
         * Con un dedo se arrastra si la pagina no cabe a lo ancho, y eso pasa
         * por dos motivos: porque este ampliada, o porque sea una **doble**.
         * Una doble puesta a alto completo mide mas que la pantalla sin
         * ampliar nada, y moverla de lado es la unica forma de leerla.
         */
        const medidas = medir()
        const desborda = medidas.ancho * vista.current.escala > medidas.anchoLienzo + 1
        gesto =
          vista.current.escala > 1 || desborda
            ? {
                tipo: 'arrastre',
                medidas,
                punto0: { x: uno.clientX, y: uno.clientY },
                vista0: { ...vista.current },
              }
            : null
      } else {
        gesto = null
      }
    }

    function alMover(e: TouchEvent) {
      if (!gesto) return
      const lienzo = caja!.getBoundingClientRect()

      if (gesto.tipo === 'pellizco') {
        const uno = e.touches[0]
        const dos = e.touches[1]
        if (!uno || !dos) return
        e.preventDefault()
        hubogesto.current = true
        const s = limpia(gesto.vista0.escala * (separacion(uno, dos) / gesto.separacion0))
        /*
         * El punto de la pagina que habia bajo los dedos se queda bajo los
         * dedos: es lo que hace que el pellizco se sienta agarrado a la
         * imagen y no a la pantalla. Y como se sigue tambien el centro, se
         * puede mover mientras se amplia, sin soltar.
         */
        const px = (gesto.centro0.x - lienzo.left - gesto.vista0.x) / gesto.vista0.escala
        const py = (gesto.centro0.y - lienzo.top - gesto.vista0.y) / gesto.vista0.escala
        const c = centro(uno, dos)
        vista.current = ajustar(
          gesto.medidas,
          s,
          c.x - lienzo.left - px * s,
          c.y - lienzo.top - py * s,
        )
        pintar()
        return
      }

      const uno = e.touches[0]
      if (!uno) return
      e.preventDefault()
      const dx = uno.clientX - gesto.punto0.x
      const dy = uno.clientY - gesto.punto0.y
      if (Math.hypot(dx, dy) > ARRASTRE) hubogesto.current = true
      vista.current = ajustar(
        gesto.medidas,
        gesto.vista0.escala,
        gesto.vista0.x + dx,
        gesto.vista0.y + dy,
      )
      pintar()
    }

    function alSoltar(e: TouchEvent) {
      // Al levantar uno de los dos dedos no se sigue con el otro: pegaria un
      // salto, porque el que queda no esta donde estaba el centro.
      gesto = null
      if (e.touches.length > 0) return
      if (vista.current.escala !== 1 && vista.current.escala < MINIMO_UTIL) {
        vista.current = { escala: 1, x: 0, y: 0 }
        pintar()
      }
      setEscala(vista.current.escala)
    }

    // `passive: false` porque hay que poder cortarle el gesto a Safari
    caja.addEventListener('touchstart', alEmpezar, { passive: false })
    caja.addEventListener('touchmove', alMover, { passive: false })
    caja.addEventListener('touchend', alSoltar)
    caja.addEventListener('touchcancel', alSoltar)
    return () => {
      caja.removeEventListener('touchstart', alEmpezar)
      caja.removeEventListener('touchmove', alMover)
      caja.removeEventListener('touchend', alSoltar)
      caja.removeEventListener('touchcancel', alSoltar)
    }
  }, [caja, contenidoRef, activo, pintar])

  const consumirGesto = useCallback(() => {
    const hubo = hubogesto.current
    hubogesto.current = false
    return hubo
  }, [])

  return { escala, consumirGesto, quitar, colocar }
}

/**
 * Pellizco en la tira vertical: se le cambia el ancho al contenedor.
 *
 * Al crecer el ancho crecen las imagenes —van al 100% de el— y con ellas el
 * alto de la tira, que es justo lo que hace falta para que el resto del lector
 * siga midiendo bien. Mover la pagina ampliada no lleva codigo: es el propio
 * desplazamiento del navegador, que ahora tambien es horizontal.
 *
 * @param caja         el scroller (`.lector-webtoon`)
 * @param contenidoRef el div de las paginas, al que se le cambia el ancho
 */
export function useZoomTira(
  caja: HTMLElement | null,
  contenidoRef: RefObject<HTMLElement | null>,
  activo: boolean,
): Zoom {
  const escalaRef = useRef(1)
  const [, avisar] = useState(0)
  const hubogesto = useRef(false)

  /**
   * Amplia dejando quieto el punto de la tira que hay bajo `ancla`.
   *
   * Todo en la tira mide en proporcion a la escala (el ancho por definicion, y
   * el alto porque las imagenes guardan su forma), asi que un punto del
   * contenido son las coordenadas del scroll divididas por la escala.
   */
  const aplicar = useCallback(
    (escala: number, ancla: Punto, punto: Punto) => {
      const contenido = contenidoRef.current
      if (!caja || !contenido) return
      escalaRef.current = escala
      // El ancho no se escribe aqui sino en el CSS, a partir de `--zoom`: en
      // pantalla ancha la tira ademas lleva un tope de ancho, y ese tope tiene
      // que crecer con el pellizco igual que lo hace el ancho.
      if (escala === 1) contenido.style.removeProperty('--zoom')
      else contenido.style.setProperty('--zoom', String(escala))
      const lienzo = caja.getBoundingClientRect()
      // Escribir el scroll obliga a recalcular la maquetacion, asi que el alto
      // nuevo ya esta hecho al colocarlo: no hay que esperar a un cuadro.
      caja.scrollLeft = punto.x * escala - (ancla.x - lienzo.left)
      caja.scrollTop = punto.y * escala - (ancla.y - lienzo.top)
    },
    [caja, contenidoRef],
  )

  const quitar = useCallback(() => {
    if (!caja || escalaRef.current === 1) return
    const lienzo = caja.getBoundingClientRect()
    const antes = escalaRef.current
    // Ancla en lo alto de lo que se esta viendo: quitar el zoom no deberia
    // mover la lectura de sitio.
    aplicar(1, { x: lienzo.left, y: lienzo.top }, { x: 0, y: caja.scrollTop / antes })
    avisar((n) => n + 1)
  }, [caja, aplicar])

  useEffect(() => {
    if (!caja || !activo) return

    let gesto: { separacion0: number; escala0: number; punto: Punto } | null = null

    function alEmpezar(e: TouchEvent) {
      hubogesto.current = false
      const uno = e.touches[0]
      const dos = e.touches[1]
      if (!uno || !dos) {
        gesto = null
        return
      }
      const lienzo = caja!.getBoundingClientRect()
      const c = centro(uno, dos)
      gesto = {
        separacion0: separacion(uno, dos),
        escala0: escalaRef.current,
        punto: {
          x: (caja!.scrollLeft + c.x - lienzo.left) / escalaRef.current,
          y: (caja!.scrollTop + c.y - lienzo.top) / escalaRef.current,
        },
      }
    }

    function alMover(e: TouchEvent) {
      if (!gesto) return
      const uno = e.touches[0]
      const dos = e.touches[1]
      if (!uno || !dos) return
      // Sin esto el navegador desplaza la tira a la vez que se pellizca
      e.preventDefault()
      hubogesto.current = true
      aplicar(
        limpia(gesto.escala0 * (separacion(uno, dos) / gesto.separacion0)),
        centro(uno, dos),
        gesto.punto,
      )
    }

    function alSoltar(e: TouchEvent) {
      gesto = null
      if (e.touches.length > 0) return
      avisar((n) => n + 1)
    }

    caja.addEventListener('touchstart', alEmpezar, { passive: false })
    caja.addEventListener('touchmove', alMover, { passive: false })
    caja.addEventListener('touchend', alSoltar)
    caja.addEventListener('touchcancel', alSoltar)
    return () => {
      caja.removeEventListener('touchstart', alEmpezar)
      caja.removeEventListener('touchmove', alMover)
      caja.removeEventListener('touchend', alSoltar)
      caja.removeEventListener('touchcancel', alSoltar)
    }
  }, [caja, activo, aplicar])

  const consumirGesto = useCallback(() => {
    const hubo = hubogesto.current
    hubogesto.current = false
    return hubo
  }, [])

  // En la tira el desplazamiento es el del navegador, asi que no hay nada que
  // colocar a mano; esta para que los dos modos den el mismo `Zoom`.
  const colocar = useCallback(() => {}, [])

  // La escala sale de la referencia y no de un estado: durante el gesto se
  // escribe en el DOM sin volver a pintar, y aun asi cualquier repintado que
  // venga de otra cosa tiene que ver el ancho de verdad.
  return { escala: escalaRef.current, consumirGesto, quitar, colocar }
}
